import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import BookOnlineOutlinedIcon from "@mui/icons-material/BookOnlineOutlined";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { FormattedMessage, useIntl } from "react-intl";
import { useRecoilValue } from "recoil";

import StyledDataGrid from "../components/Table/StyledDataGrid";
import { formatterService } from "../NumberFormatter";
import { ImpactReport, ImpactSample } from "./types";

interface Props {
  report: ImpactReport | null;
  isLoading: boolean;
  /**
   * One sentence instead of the tiles and the detailed list. Inside a form
   * whose job is to define a scope, the simulation answers one question —
   * how many prices move and which way — and a report of eight figures and
   * a scrolling table pushes the form it belongs to off the screen.
   */
  compact?: boolean;
  /** Called when the analyst asks for another page of the detailed list. */
  onPage?: (offset: number) => void;
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 20;
// Six rows of detail is enough to recognise the change without the list taking
// over the dialog; the rest is one page away.
const DETAIL_GRID_HEIGHT = 320;

const Metric = ({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) => (
  <Box sx={{ minWidth: 96 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block" }}
    >
      <FormattedMessage id={label} />
    </Typography>
    <Typography variant="h5" color={emphasis ? "warning.main" : "text.primary"}>
      {value}
    </Typography>
  </Box>
);

const ImpactSummary = ({
  report,
  isLoading,
  compact,
  onPage,
  pageSize,
}: Props) => {
  const intl = useIntl();
  const formatter = useRecoilValue(formatterService);

  const currency = (value: string) =>
    formatter.format(Number(value), {
      style: "currency",
      maximumFractionDigits: 0,
    });
  const percent = (value: number | null) =>
    value === null ? "—" : `${value > 0 ? "+" : ""}${value.toFixed(1)} %`;

  if (isLoading) {
    return <CircularProgress size={20} />;
  }
  if (!report) {
    return null;
  }

  if (compact) {
    return (
      <Typography variant="body2" color="text.secondary">
        <FormattedMessage
          id="PRICING_TIER_IMPACT_SENTENCE"
          values={{
            moves: report.dates_off_grid,
            up: report.moves_up,
            down: report.moves_down,
            average: percent(report.avg_delta_pct),
          }}
        />
      </Typography>
    );
  }

  const size = pageSize ?? DEFAULT_PAGE_SIZE;
  const columns: GridColDef[] = [
    {
      field: "occupation_date",
      headerName: intl.formatMessage({ id: "DATE" }),
      width: 120,
    },
    {
      field: "uh_cat_short_name",
      headerName: intl.formatMessage({ id: "UH_CAT" }),
      flex: 1,
      minWidth: 120,
    },
    {
      field: "current_price_gross",
      headerName: intl.formatMessage({ id: "PRICING_TIER_IMPACT_FROM" }),
      width: 110,
      align: "right",
      headerAlign: "right",
      renderCell: (params: GridRenderCellParams<ImpactSample>) => (
        <Typography variant="body2">
          {currency(params.row.current_price_gross)}
        </Typography>
      ),
    },
    {
      field: "tier_price_gross",
      headerName: intl.formatMessage({ id: "PRICING_TIER_IMPACT_TO" }),
      width: 110,
      align: "right",
      headerAlign: "right",
      renderCell: (params: GridRenderCellParams<ImpactSample>) => (
        <Typography variant="body2">
          {currency(params.row.tier_price_gross)}
        </Typography>
      ),
    },
    {
      field: "level",
      headerName: intl.formatMessage({ id: "PRICING_TIER_LEVEL" }),
      width: 90,
      renderCell: (params: GridRenderCellParams<ImpactSample>) => (
        <Typography variant="body2">
          {params.row.level_label ?? params.row.level}
        </Typography>
      ),
    },
    {
      field: "flags",
      headerName: "",
      width: 72,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ImpactSample>) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {params.row.is_locked && (
            <LockOutlinedIcon
              fontSize="small"
              color="warning"
              titleAccess={intl.formatMessage({
                id: "PRICING_TIER_IMPACT_LOCKED_ROW",
              })}
            />
          )}
          {params.row.is_booked && (
            <BookOnlineOutlinedIcon
              fontSize="small"
              color="warning"
              titleAccess={intl.formatMessage({
                id: "PRICING_TIER_IMPACT_BOOKED_ROW",
              })}
            />
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Metric
          label="PRICING_TIER_IMPACT_COVERED"
          value={String(report.dates_covered)}
        />
        <Metric
          label="PRICING_TIER_IMPACT_OFF_LADDER"
          value={String(report.dates_off_grid)}
        />
        <Metric
          label="PRICING_TIER_IMPACT_MOVES_UP"
          value={String(report.moves_up)}
        />
        <Metric
          label="PRICING_TIER_IMPACT_MOVES_DOWN"
          value={String(report.moves_down)}
        />
        <Metric
          label="PRICING_TIER_IMPACT_LOCKED"
          value={String(report.dates_locked)}
          emphasis={report.dates_locked > 0}
        />
        <Metric
          label="PRICING_TIER_IMPACT_BOOKED"
          value={String(report.dates_booked)}
          emphasis={report.dates_booked > 0}
        />
        <Metric
          label="PRICING_TIER_IMPACT_AVG_DELTA"
          value={percent(report.avg_delta_pct)}
        />
        <Metric
          label="PRICING_TIER_IMPACT_MAX_DELTA"
          value={percent(report.max_delta_pct)}
        />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {report.dates_excluded > 0 && (
          <Chip
            size="small"
            icon={<EventBusyOutlinedIcon />}
            label={
              <FormattedMessage
                id="PRICING_TIER_IMPACT_EXCLUDED"
                values={{ count: report.dates_excluded }}
              />
            }
          />
        )}
        {report.at_ceiling > 0 && (
          <Chip
            size="small"
            color="warning"
            label={
              <FormattedMessage
                id="PRICING_TIER_IMPACT_AT_CEILING"
                values={{ count: report.at_ceiling }}
              />
            }
          />
        )}
        {report.at_floor > 0 && (
          <Chip
            size="small"
            color="warning"
            label={
              <FormattedMessage
                id="PRICING_TIER_IMPACT_AT_FLOOR"
                values={{ count: report.at_floor }}
              />
            }
          />
        )}
      </Box>

      {report.truncated && (
        <Typography variant="caption" color="warning.main">
          <FormattedMessage id="PRICING_TIER_IMPACT_TRUNCATED" />
        </Typography>
      )}

      {report.samples.length > 0 && (
        <Box sx={{ height: DETAIL_GRID_HEIGHT }}>
          <StyledDataGrid
            rows={report.samples}
            columns={columns}
            // The scan has no row id of its own; the cell's own key is one.
            getRowId={(row) =>
              `${row.uh_cat_id}-${row.grid_type_id}-${row.occupation_date}`
            }
            disableRowSelectionOnClick
            // Server-side: the dialog holds one page, the scan holds the rest.
            paginationMode="server"
            rowCount={report.dates_off_grid}
            paginationModel={{
              page: Math.floor(report.samples_offset / size),
              pageSize: size,
            }}
            pageSizeOptions={[size]}
            onPaginationModelChange={(model) =>
              onPage?.(model.page * model.pageSize)
            }
            hideFooter={onPage === undefined}
          />
        </Box>
      )}
    </Box>
  );
};

export default ImpactSummary;
